import html2pdf from "html2pdf.js";
import { userLoggedIn } from "@/core/consts";

/** Largura útil de A4 paisagem com margem de 8 mm (≈ 1062 px @ 96 dpi). */
const PDF_CONTENT_WIDTH_PX = Math.floor(((297 - 16) / 25.4) * 96);

const PDF_EXPORT_STYLE_ID = "kpi-pdf-export-styles";

const PDF_EXPORT_CSS = `
.kpi-pdf-export,
.kpi-pdf-export * {
  box-sizing: border-box;
}

.kpi-pdf-export {
  width: ${PDF_CONTENT_WIDTH_PX}px !important;
  max-width: ${PDF_CONTENT_WIDTH_PX}px !important;
  overflow: visible !important;
}

.kpi-pdf-export .bloco {
  overflow: visible !important;
  max-width: 100% !important;
}

.kpi-pdf-export [class*="grid-4"] {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  gap: 8px !important;
}

.kpi-pdf-export [class*="grid-3"] {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 8px !important;
}

.kpi-pdf-export [class*="grid-2"] {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 8px !important;
}

.kpi-pdf-export .rel-dash__kpi,
.kpi-pdf-export .rel-dash__grafico-card,
.kpi-pdf-export .rel-dash__consumo-card {
  min-width: 0 !important;
  max-width: 100% !important;
}

.kpi-pdf-export .rel-dash__kpi-label {
  font-size: 0.62rem !important;
  line-height: 1.2 !important;
  word-break: break-word !important;
}

.kpi-pdf-export .rel-dash__kpi-valor {
  font-size: 0.95rem !important;
}

.kpi-pdf-export .rel-dash__grafico-card h4 {
  font-size: 0.68rem !important;
}

.kpi-pdf-export .rel-dash__plot--sm {
  height: 160px !important;
}

.kpi-pdf-export .rel-dash__plot--sm img {
  width: 100% !important;
  height: auto !important;
  max-height: 160px !important;
  object-fit: contain !important;
}

.kpi-pdf-export .rel-dash__table-wrap {
  overflow: visible !important;
  max-width: 100% !important;
}

.kpi-pdf-export .rel-dash__tabela {
  width: 100% !important;
  max-width: 100% !important;
  table-layout: fixed !important;
  font-size: 0.72rem !important;
}

.kpi-pdf-export .rel-dash__tabela th,
.kpi-pdf-export .rel-dash__tabela td {
  white-space: normal !important;
  word-break: break-word !important;
  padding: 5px 6px !important;
}

.kpi-pdf-export .rel-dash__tabela--eventos {
  font-size: 0.62rem !important;
}

.kpi-pdf-export .rel-dash__section,
.kpi-pdf-export .rel-dash__maq-section {
  page-break-inside: avoid;
  break-inside: avoid;
}

.kpi-pdf-export .rel-dash__usinagem-body {
  grid-template-columns: 200px 1fr !important;
}
`;

function ensurePdfExportStyles() {
  if (document.getElementById(PDF_EXPORT_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PDF_EXPORT_STYLE_ID;
  style.textContent = PDF_EXPORT_CSS;
  document.head.appendChild(style);
}

function waitForImages(root) {
  const images = [...root.querySelectorAll("img")];
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })
    )
  );
}

/**
 * Clona o elemento e substitui cada canvas (Chart.js) por uma imagem,
 * pois cloneNode não copia o bitmap desenhado no canvas.
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
function cloneElementForPdf(element) {
  const cloned = element.cloneNode(true);
  cloned.classList.add("kpi-pdf-export");
  cloned.style.display = "block";
  cloned.style.width = `${PDF_CONTENT_WIDTH_PX}px`;
  cloned.style.maxWidth = `${PDF_CONTENT_WIDTH_PX}px`;

  const sourceCanvases = element.querySelectorAll("canvas");
  const clonedCanvases = cloned.querySelectorAll("canvas");

  clonedCanvases.forEach((cloneCanvas, index) => {
    const sourceCanvas = sourceCanvases[index];
    if (!sourceCanvas) return;

    try {
      const img = document.createElement("img");
      img.src = sourceCanvas.toDataURL("image/png");
      img.alt = "";
      img.style.display = "block";
      img.style.width = "100%";
      img.style.height = "auto";
      img.style.maxWidth = "100%";
      cloneCanvas.replaceWith(img);
    } catch {
      // Canvas tainted or empty — mantém o elemento original no clone.
    }
  });

  return cloned;
}

/**
 * Gera PDF a partir de um elemento DOM (mesmo padrão de OpPdfTemplate.vue).
 * @param {HTMLElement} element
 * @param {string} filename
 */
export async function exportElementToPdf(element, filename) {
  if (!element) {
    throw new Error("Elemento para exportação não encontrado.");
  }

  ensurePdfExportStyles();

  const opt = {
    margin: [8, 8, 12, 8],
    filename,
    image: { type: "jpeg", quality: 0.95 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: PDF_CONTENT_WIDTH_PX,
      windowWidth: PDF_CONTENT_WIDTH_PX,
      scrollX: 0,
      scrollY: -window.scrollY,
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
    pagebreak: {
      mode: ["css", "legacy"],
      before: [".rel-dash__section", ".rel-dash__maq-section"],
      avoid: [".rel-dash__grafico-card", ".rel-dash__plot"],
    },
  };

  const holder = document.createElement("div");
  holder.style.position = "fixed";
  holder.style.left = "-12000px";
  holder.style.top = "0";
  holder.style.width = `${PDF_CONTENT_WIDTH_PX}px`;
  holder.style.background = "#fff";
  holder.style.zIndex = "-1";

  const cloned = cloneElementForPdf(element);
  holder.appendChild(cloned);
  document.body.appendChild(holder);

  try {
    await waitForImages(cloned);
    // Aguarda layout estável após injetar estilos e imagens dos gráficos.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const captureWidth = Math.max(PDF_CONTENT_WIDTH_PX, cloned.scrollWidth);
    opt.html2canvas.width = captureWidth;
    opt.html2canvas.windowWidth = captureWidth;

    await html2pdf()
      .set(opt)
      .from(cloned)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const usuario = userLoggedIn?.nome || "Usuário";
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setTextColor(120);
          const footer = `PDF gerado em ${new Date().toLocaleString("pt-BR")} por ${usuario} — Página ${i} de ${totalPages}`;
          pdf.text(footer, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 6, {
            align: "center",
          });
        }
      })
      .save();
  } finally {
    holder.remove();
  }
}
